import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEventPreview } from "../../services/eventService";

const PreviewEvent = () => {

  const { eventId } = useParams();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState(null);

  useEffect(() => {

    const fetchEventPreview = async () => {

      try {

        const res = await getEventPreview(eventId);
        setEventData(res.data);

      } catch (error) {
        console.error("Failed to fetch event preview", error);
      }

    };

    fetchEventPreview();

  }, [eventId]);

  if (!eventData) return <div className="p-6">Loading...</div>;

  const event = eventData.eventData || {};
  const artists = eventData.eventArtists || [];

  const venues = (eventData.eventVenueSchedules || []).map((v) => {

    const schedule = v.venueSchedules?.[0] || {};

    const startDate = schedule.eventStartDateTime
      ? new Date(schedule.eventStartDateTime)
      : null;

    return {
      venueName: v.eventVenue?.venueName,
      description: v.eventVenue?.venueDescription,
      contactPerson: v.eventVenue?.contactName,
      contactNo: v.eventVenue?.contactNumber,
      designation: v.eventVenue?.contactDesignation,
      address1: v.venueAddress?.addressLine1,
      address2: v.venueAddress?.addressLine2,
      duration: schedule.eventDuration,
      date: startDate ? startDate.toLocaleDateString() : "",
      time: startDate
        ? startDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "",
    };

  });

  const digitalEvent = event.isDigitalEvent;
  const featureEvent = event.isFeaturedEvent;
  const multipleVenues = event.eventNature === "MULTI_VENUE";

  const organizerName = event.organizerName;
  const contactPerson = event.contactPerson;
  const contactNo = event.contactNo;

  const city = event.cityName;
  const category = eventData.categoryName;
  const genre = event.eventCategoryName;
  const description = event.eventDescription;
  const entryType = event.eventType;
  const language = event.language;

  return (

    <div className="px-4 md:px-0 py-5">

      {/* Breadcrumb */}
      <div className="text-xs mb-4 flex gap-1">
        <span className="text-gray-400">Home /</span>
        <span className="text-black font-medium">Create an Event</span>
      </div>

      {/* Step Indicator */}
      <div className="flex flex-wrap items-center justify-start gap-y-6 mb-10">

        {[1, 2, 3, 4].map((step, index) => (
          <div key={step} className="flex items-center">

            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold
              ${step === 1 ? "bg-[#65AE5C] text-white" :
              step === 2 ? "bg-[#65AE5C] text-white" :
              step === 3 ? "bg-[#65AE5C] text-white" :
              step === 4 ? "bg-yellow-400 text-white" : "bg-gray-300 text-white"}`}
            >
              {step}
            </div>

            {index !== 3 && (
              <div className="flex items-center mx-2 sm:mx-4">
                <div className="w-8 sm:w-16 h-[2px] bg-neutral-400"></div>
                <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[8px] border-t-transparent border-b-transparent border-l-neutral-400"></div>
              </div>
            )}

          </div>
        ))}

      </div>

      <h1 className="text-2xl font-semibold mb-5">Create an Event</h1>
      <h1 className="text-xl font-semibold mb-5">Preview</h1>

      {/* EVENT TYPE */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">

        <div>
          <div className="font-semibold">Event Type</div>
          <div className="text-stone-500">
            Digital Event <span className="ml-2 font-semibold text-black">
              {digitalEvent ? "YES" : "NO"}
            </span>
          </div>
        </div>

        <div>
          <div className="font-semibold">Event Details</div>
          <div className="text-stone-500">
            Feature Event <span className="ml-2 font-semibold text-black">
              {featureEvent ? "YES" : "NO"}
            </span>
          </div>
        </div>

        <div className="md:mt-9 text-stone-500">
          Multiple Venues <span className="ml-2 font-semibold text-black">
            {multipleVenues ? "YES" : "NO"}
          </span>
        </div>

      </div>

      {/* ORGANIZER DETAILS */}
      <div className="mb-10">

        <div className="text-2xl font-semibold mb-4">
          Organizer Detail
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">

          <div>
            <span className="text-stone-500">Organizer’s Name: </span>
            <span className="font-semibold">{organizerName}</span>
          </div>

          <div>
            <span className="text-stone-500">Contact No.: </span>
            <span className="font-semibold">{contactNo}</span>
          </div>

          <div>
            <span className="text-stone-500">Contact Person: </span>
            <span className="font-semibold">{contactPerson}</span>
          </div>

        </div>

      </div>

      {/* EVENT BANNER */}
      <img
        className="w-full h-56 md:h-96 mb-10 object-cover"
        src="https://placehold.co/1190x371"
        alt="event banner"
      />

      {/* EVENT DETAILS */}
      <div className="mb-12 text-base">

        <div>
          <span className="text-zinc-600">City: </span>
          <span className="font-semibold">{city}</span>
          <span className="ml-3 text-zinc-600">Category: </span>
          <span className="font-semibold">{category}</span>
        </div>

        <h1 className="text-2xl my-4">{event.eventName}</h1>

        <div>
          <span className="text-zinc-600">Genre: </span>
          <span className="font-semibold">{genre}</span>
        </div>

        <p className="text-stone-500 mt-2 leading-6">
          {description}
        </p>

        <div className="mt-4">
          <span className="text-zinc-600">Mode Of Entry: </span>
          <span className="font-semibold">{entryType}</span>
        </div>

        <div>
          <span className="text-zinc-600">Language: </span>
          <span className="font-semibold">{language}</span>
        </div>

      </div>

      {/* CAST / CREW */}
      <div className="mb-12">

        <div className="text-xl font-semibold mb-6">
          Cast/Crew Details
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10">

          {artists.map((artist, index) => (

            <div key={index}>

              <img
                className="w-full max-w-[220px] h-56 rounded-md mb-4 object-cover"
                src={artist?.artistImages?.[0]?.imageUrl || "https://placehold.co/229x221"}
                alt={artist.artistName}
              />

              <div>

                <div className="text-xl font-semibold">
                  {artist.artistName}
                </div>

                <div className="text-sm">
                  {artist.artistRole}
                </div>

                <p className="text-sm mt-2">
                  {artist.description}
                </p>

              </div>

            </div>

          ))}

        </div>

      </div>

      {/* VENUES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">

        {venues.map((venue, index) => (

          <div key={index} className="mb-10">

            <div className="text-lg font-semibold mb-4">
              Venue {index + 1}
            </div>

            <div className="text-xs text-stone-500">
              Venue Name <span className="font-semibold">{venue.venueName}</span>
            </div>

            <div className="text-xs text-stone-500 mt-2">
              Venue Description
            </div>

            <div className="text-xs text-stone-500">
              {venue.address1} {venue.address2}
            </div>

            <div className="text-xs text-stone-500 mt-2">
              Contact Person:
              <span className="font-semibold"> {venue.contactPerson}</span>
            </div>

            <div className="text-xs text-stone-500">
              Contact Number:
              <span className="font-semibold"> {venue.contactNo}</span>
            </div>

            <div className="text-xs text-stone-500">
              Designation:
              <span className="font-semibold"> {venue.designation}</span>
            </div>

            <div className="mt-4 font-semibold text-base">
              Duration
            </div>

            <div className="text-xs text-stone-500">
              {venue.duration} Minutes
            </div>

            <div className="mt-4 font-semibold text-base">
              Date and Time Slot
            </div>

            <div className="text-xs text-stone-500">
              {venue.date} , {venue.time}
            </div>

          </div>

        ))}

      </div>

      {/* SUBMIT BUTTON */}
      <div className="flex justify-end mt-10">
        <div
          onClick={() => navigate("/event-created")}
          className="cursor-pointer w-full sm:w-80 h-12 px-6 py-3 bg-green-700 flex justify-center items-center"
        >
          <div className="text-white text-base font-bold uppercase">
            Confirm and Submit
          </div>
        </div>
      </div>

    </div>

  );
};

export default PreviewEvent;